// Models
import Model from './Model'
import camelCase from 'lodash/camelCase'

class Log extends Model {
  get formatters () {
    return {
      datetime: data => data.replace('Z', '')
    }
  }

  get isAccountCreated () {
    return this.action === PROFILE_ACTIONS.ACCOUNT_CREATED
  }

  get isAccountBlockedOrUnblocked () {
    return this.action === PROFILE_ACTIONS.ACCOUNT_BLOCKED || this.action === PROFILE_ACTIONS.ACCOUNT_UNBLOCKED
  }

  get isAccountTypeChanged () {
    return this.action === PROFILE_ACTIONS.ACCOUNT_TYPE_CHANGED
  }

  get isFeeToggle () {
    return [PROFILE_ACTIONS.FEE_FROZEN, PROFILE_ACTIONS.FEE_UNFROZEN].includes(this.action)
  }

  get isTransactionUpdate () {
    return this.action === TRANSACTION_ACTIONS.UPDATED
  }

  get isTransactionSettled () {
    return this.action === TRANSACTION_ACTIONS.SETTLED
  }

  get isTransactionManuallySettled () {
    return this.action === TRANSACTION_ACTIONS.MANUALLY_SETTLED
  }

  get isPaymentSigned () {
    return this.action === PAYMENT_ACTIONS.SIGNED
  }

  get isReturnRequested () {
    return this.action === TRANSACTION_ACTIONS.RETURN_REQUESTED
  }

  get hasData () {
    return this.data != null
  }

  get hasPaymentId () {
    return this.hasData && this.data.paymentId != null
  }

  get hasOperationId () {
    return this.hasData && this.data.operationId != null
  }

  get hasTemplateId () {
    return this.hasData && this.data.templateId != null
  }

  get hasDataDiffs () {
    return this.hasData && Object.keys(this.dataDiffs).length > 0
  }

  get hasTransactionDetails () {
    return this.transactionDetails.length > 0
  }

  get author () {
    return this.profileId || this.userId || this.adminId || this.serviceId
  }

  get statusData () {
    const {
      status,
      sepaReasonDescription,
      amlStatus,
      matchStatus,
      screeningResult
    } = this.data

    if (status) {
      return status
    }

    if (sepaReasonDescription) {
      return sepaReasonDescription
    }

    if (amlStatus) {
      return (matchStatus != null)
        ? `${amlStatus} (${matchStatus})`
        : amlStatus
    }

    if (screeningResult) {
      return screeningResult
    }
  }

  get transactionDetails () {
    const transactionDetails = []

    if (!this.hasData) {
      return transactionDetails
    }

    if (this.data.direction != null) {
      transactionDetails.push(this.data.direction)
    }

    if (this.data.type != null) {
      transactionDetails.push(this.data.type)
    }

    if (this.data.return) {
      transactionDetails.push('RETURN')
    }

    if (this.data.channel != null) {
      transactionDetails.push(this.data.channel)
    } else if (this.data.oldChannel != null && this.data.newChannel != null) {
      transactionDetails.push(`${this.data.oldChannel} → ${this.data.newChannel}`)
    } else if (this.data.newChannel != null) {
      transactionDetails.push(this.data.newChannel)
    }

    return transactionDetails
  }

  get dataDiffs () {
    return this.data.changes != null ? this.dataNewDiffs : this.dataLegacyDiffs
  }

  get dataNewDiffs () {
    if (this.data == null || this.data.changes == null) {
      return {}
    }

    return this.data.changes.reduce((diff, change) => {
      diff[change.field] = {
        oldValue: Array.isArray(change.old) ? `[${change.old.join(', ')}]` : String(change.old),
        newValue: Array.isArray(change.new) ? `[${change.new.join(', ')}]` : String(change.new)
      }

      return diff
    }, {})
  }

  get dataLegacyDiffs () {
    return Object.keys(this.data).reduce((diffs, prop) => {
      if (!prop.startsWith('old')) {
        return diffs
      }

      const field = prop.substring(3)

      if (!this.data[`new${field}`] || this.data[`old${field}`] === this.data[`new${field}`]) {
        return diffs
      }

      diffs[camelCase(field)] = {
        oldValue: this.data[`old${field}`],
        newValue: this.data[`new${field}`]
      }

      return diffs
    }, {})
  }

  get schema () {
    return [
      'ID',
      'action',
      'adminId',
      'data',
      'datetime',
      'ip',
      'ipCountry',
      'ipCity',
      'ray',
      'profileId',
      'projectName',
      'serviceId',
      'userAgent',
      'userId'
    ]
  }
}

export default Log

export const PAYMENT_ACTIONS = {
  SIGNED: 'paymentSigned'
}

export const TRANSACTION_ACTIONS = {
  CHANNEL_CHANGED: 'transactionChannelChanged',
  UPDATED: 'transactionUpdated',
  SETTLED: 'transactionSettled',
  MANUALLY_SETTLED: 'transactionManuallySettled',
  RETURN_REQUESTED: 'returnRequested'
}

export const PROFILE_ACTIONS = {
  ACCOUNT_CREATED: 'accountCreated',
  ACCOUNT_BLOCKED: 'accountBlocked',
  ACCOUNT_UNBLOCKED: 'accountUnblocked',
  ACCOUNT_TYPE_CHANGED: 'accountTypeChanged',
  FEE_FROZEN: 'feeFrozen',
  FEE_UNFROZEN: 'feeUnfrozen'
}

export const PROJECT_NAMES = {
  OPEN_BANKING_INTEGRATION: 'open-banking-integration'
}
